import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Avatar,
  useTheme,
  Fab,
  Menu
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as ActiveIcon,
  CheckCircle as CompletedIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import TimeEntryDialog from './TimeEntryDialog';
import { formatScheduledDate, todayLocalYmd } from '../../utils/dateUtils';

const TimesheetManagement = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State for data
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalEntries, setTotalEntries] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    employee_id: '',
    status: '',
    start_date: '',
    end_date: '',
    search: '',
    sort_by: 'clock_in_time',
    sort_order: 'DESC'
  });
  
  // Summary stats
  const [summary, setSummary] = useState({
    total_entries: 0,
    active_entries: 0,
    completed_entries: 0,
    total_hours: '0.00',
    unique_employees: 0,
    unique_days: 0
  });
  
  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Quick date filters
  const [dateRangeFilter, setDateRangeFilter] = useState('7');
  
  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEntryId, setMenuEntryId] = useState(null);

  // Load timesheet entries
  const loadEntries = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      
      const response = await makeApiCall(`api/time-tracking/admin/entries?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries);
        setTotalEntries(data.pagination.total);
        setSummary(data.summary);
      } else {
        throw new Error('Failed to load timesheet entries');
      }
      
    } catch (error) {
      console.error('Error loading entries:', error);
      setError('Failed to load timesheet entries');
    } finally {
      setLoading(false);
    }
  };

  // Load employees for dropdown
  const loadEmployees = async () => {
    try {
      const response = await makeApiCall('api/time-tracking/admin/employees-list');
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  // Handle date range filter
  const handleDateRangeFilter = (days) => {
    setDateRangeFilter(days);
    
    if (days === 'custom') {
      // Don't set dates automatically for custom
      return;
    }
    
    // Local calendar days, not UTC: toISOString() rolled the range forward a
    // day for evening users in Ontario.
    const endDate = todayLocalYmd();
    
    let startDate = '';
    if (days !== 'all') {
      const start = new Date();
      start.setDate(start.getDate() - parseInt(days));
      startDate = todayLocalYmd(start);
    }
    
    setFilters(prev => ({
      ...prev,
      start_date: startDate,
      end_date: endDate
    }));
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  // Handle sorting
  const handleSort = (field) => {
    const newOrder = filters.sort_by === field && filters.sort_order === 'DESC' ? 'ASC' : 'DESC';
    setFilters(prev => ({
      ...prev,
      sort_by: field,
      sort_order: newOrder
    }));
  };

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for display
  // work_date is a Postgres DATE that arrives as UTC midnight; parsing it with
  // new Date() showed Monday's clock-ins as Sunday in Ontario.
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatScheduledDate(dateString, 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format hours for display
  const formatHours = (hours) => {
    if (!hours || hours === 0) return '0.0h';
    return `${parseFloat(hours).toFixed(1)}h`;
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    if (status === 'active') {
      return {
        color: 'warning',
        icon: <ActiveIcon />,
        text: 'In Progress'
      };
    }
    return {
      color: 'success', 
      icon: <CompletedIcon />,
      text: 'Complete'
    };
  };

  // Handle menu open/close
  const handleMenuClick = (event, entryId) => {
    setAnchorEl(event.currentTarget);
    setMenuEntryId(entryId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuEntryId(null);
  };

  // CRUD operations
  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleDelete = (entry) => {
    setSelectedEntry(entry);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!selectedEntry) return;
    
    try {
      const response = await makeApiCall(`api/time-tracking/admin/entries/${selectedEntry.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setSuccess('Time entry deleted successfully');
        loadEntries();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete time entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      setError('Failed to delete time entry');
    }
    
    setOpenDeleteDialog(false);
    setSelectedEntry(null);
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    loadEntries();
  }, [page, rowsPerPage, filters]);

  // Summary stats cards data
  const statsCards = [
    {
      title: 'Total Hours',
      value: `${summary.total_hours}h`,
      icon: <AccessTimeIcon />,
      color: 'primary.main'
    },
    {
      title: 'Active Sessions',
      value: summary.active_entries,
      icon: <ActiveIcon />,
      color: 'warning.main'
    },
    {
      title: 'Completed Sessions',
      value: summary.completed_entries,
      icon: <CompletedIcon />,
      color: 'success.main'
    },
    {
      title: 'Employees',
      value: summary.unique_employees,
      icon: <PersonIcon />,
      color: 'info.main'
    },
    {
      title: 'Days Covered',
      value: summary.unique_days,
      icon: <CalendarIcon />,
      color: 'secondary.main'
    },
    {
      title: 'Total Entries',
      value: summary.total_entries,
      icon: <ScheduleIcon />,
      color: 'text.secondary'
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-demo-target="page-timesheets-root">
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          mb: 3
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#000062',
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}
        >
          Timesheet Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
          sx={{ 
            borderRadius: 2,
            minWidth: { xs: '100%', sm: 'auto' }
          }}
        >
          New Entry
        </Button>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={{ xs: 2, md: 2 }} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
            <Card>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      color="textSecondary" 
                      gutterBottom 
                      variant="overline"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        color: card.color,
                        fontSize: '1.25rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: card.color, 
                    width: 40, 
                    height: 40,
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon /> Filters & Search
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Employee name, notes..."
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select
                  value={filters.employee_id}
                  label="Employee"
                  onChange={(e) => handleFilterChange('employee_id', e.target.value)}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.employee_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">In Progress</MenuItem>
                  <MenuItem value="completed">Complete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRangeFilter}
                  label="Date Range"
                  onChange={(e) => handleDateRangeFilter(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 3 months</MenuItem>
                  <MenuItem value="365">Last year</MenuItem>
                  <MenuItem value="all">All time</MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {dateRangeFilter === 'custom' && (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    size="small"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    size="small"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            
            <Grid size="auto">
              <Tooltip title="Refresh">
                <IconButton 
                  onClick={loadEntries} 
                  color="primary" 
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Timesheet Entries Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Desktop Table */}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Button
                        startIcon={<SortIcon />}
                        onClick={() => handleSort('employee_name')}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Employee
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        startIcon={<SortIcon />}
                        onClick={() => handleSort('work_date')}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Date
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        startIcon={<SortIcon />}
                        onClick={() => handleSort('clock_in_time')}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Clock In
                      </Button>
                    </TableCell>
                    <TableCell>Clock Out</TableCell>
                    <TableCell>
                      <Button
                        startIcon={<SortIcon />}
                        onClick={() => handleSort('hours_worked')}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Hours
                      </Button>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        Loading timesheet entries...
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        No timesheet entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry) => {
                      const statusDisplay = getStatusDisplay(entry.status);
                      return (
                        <TableRow key={entry.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                {entry.employee_name?.[0] || entry.username?.[0] || 'U'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {entry.employee_name || `${entry.first_name} ${entry.last_name}`.trim() || entry.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  @{entry.username}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(entry.work_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTime(entry.clock_in_time)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTime(entry.clock_out_time)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ fontWeight: 'medium', color: 'primary.main' }}
                            >
                              {formatHours(entry.hours_worked)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={statusDisplay.icon}
                              label={statusDisplay.text}
                              color={statusDisplay.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {entry.notes || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, entry.id)}
                              color="primary"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, p: 2 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                Loading timesheet entries...
              </Box>
            ) : entries.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                No timesheet entries found
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {entries.map((entry) => {
                  const statusDisplay = getStatusDisplay(entry.status);
                  return (
                    <Card key={entry.id} variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                              {entry.employee_name?.[0] || entry.username?.[0] || 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {entry.employee_name || `${entry.first_name} ${entry.last_name}`.trim() || entry.username}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(entry.work_date)} • @{entry.username}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, entry.id)}
                            color="primary"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Clock In: {formatTime(entry.clock_in_time)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Clock Out: {formatTime(entry.clock_out_time)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography 
                              variant="h6" 
                              color="primary" 
                              sx={{ fontWeight: 'bold', mb: 0.5 }}
                            >
                              {formatHours(entry.hours_worked)}
                            </Typography>
                            <Chip
                              icon={statusDisplay.icon}
                              label={statusDisplay.text}
                              color={statusDisplay.color}
                              size="small"
                            />
                          </Box>
                        </Box>
                        
                        {entry.notes && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Notes:</strong> {entry.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
          
          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalEntries}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 150 }
        }}
      >
        <MenuItem 
          onClick={() => {
            const entry = entries.find(e => e.id === menuEntryId);
            handleEdit(entry);
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => {
            const entry = entries.find(e => e.id === menuEntryId);
            handleDelete(entry);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Time Entry</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this time entry? This action cannot be undone.
          </Typography>
          {selectedEntry && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Employee:</strong> {selectedEntry.employee_name || selectedEntry.username}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(selectedEntry.work_date)}
              </Typography>
              <Typography variant="body2">
                <strong>Hours:</strong> {formatHours(selectedEntry.hours_worked)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', sm: 'none' }
        }}
        onClick={() => setOpenCreateDialog(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create/Edit Time Entry Dialogs */}
      <TimeEntryDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        employees={employees}
        onSuccess={(message) => {
          setSuccess(message);
          loadEntries();
        }}
      />

      <TimeEntryDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedEntry(null);
        }}
        entry={selectedEntry}
        employees={employees}
        onSuccess={(message) => {
          setSuccess(message);
          loadEntries();
          setSelectedEntry(null);
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimesheetManagement;
